import { useState } from "react";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import type { ProjectCheckoutDeletionPreview } from "@cmmd-forge/contracts";
import type {
  ProjectGroupRemovalResult,
  ProjectGroupRemovalTarget,
} from "~/projectGroupRemovalRunner";
import {
  ProjectCheckoutDeletionControl,
  ProjectGroupRemovalTargetList,
  projectGroupRemovalTargetKey,
} from "./ProjectCheckoutDeletionControls";

export interface ProjectGroupRemovalDialogProps {
  readonly open: boolean;
  readonly mode: "checkout" | "project";
  readonly projectName: string;
  readonly targets: readonly ProjectGroupRemovalTarget[];
  readonly isExecuting: boolean;
  readonly isPreviewing?: boolean;
  readonly results: readonly ProjectGroupRemovalResult[];
  readonly previews?: ReadonlyMap<string, ProjectCheckoutDeletionPreview>;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDeleteFilesChange?: (checked: boolean) => void;
  readonly onConfirm: (input: {
    readonly deleteFiles: boolean;
    readonly confirmation: string;
  }) => void;
}

export function projectGroupRemovalResultLabel(result: ProjectGroupRemovalResult): string {
  if (result.metadata === "removed") {
    return result.fileDeletion === "deleted"
      ? "Checkout files deleted. Removed from Forge."
      : "Checkout files preserved. Removed from Forge.";
  }
  if (result.fileDeletion === "blocked" || result.fileDeletion === "failed") {
    return result.message ?? "Checkout files were not removed.";
  }
  if (result.fileDeletion === "not-attempted") {
    return "Not attempted after an earlier checkout failed.";
  }
  return result.metadata === "failed" ? (result.message ?? "Forge removal failed.") : "";
}

const DIRTY_WORK_CONFIRMATION = "delete uncommitted work";

export function canConfirmProjectGroupRemoval(input: {
  readonly deleteFiles: boolean;
  readonly targets: readonly ProjectGroupRemovalTarget[];
  readonly previews: ReadonlyMap<string, ProjectCheckoutDeletionPreview>;
  readonly confirmation: string;
}): boolean {
  if (!input.deleteFiles) return true;
  return input.targets.every((target) => {
    const preview = input.previews.get(projectGroupRemovalTargetKey(target));
    if (!preview || preview.method === "unavailable" || preview.fingerprint === null) return false;
    if (preview.blockers.some((blocker) => blocker !== "dirty_git_state")) return false;
    return !preview.dirty || input.confirmation.trim() === DIRTY_WORK_CONFIRMATION;
  });
}

export function projectGroupRemovalActionLabel(input: {
  readonly mode: "checkout" | "project";
  readonly deleteFiles: boolean;
  readonly previews: ReadonlyMap<string, ProjectCheckoutDeletionPreview>;
}): string {
  const subject = input.mode === "checkout" ? "checkout" : "project";
  if (!input.deleteFiles) return `Remove ${subject}`;
  const force = [...input.previews.values()].some((preview) => preview.dirty);
  return `${force ? "Force delete" : "Delete"} files and remove ${subject}`;
}

export function ProjectGroupRemovalDialog(props: ProjectGroupRemovalDialogProps) {
  const [deleteFiles, setDeleteFiles] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const action = props.mode === "checkout" ? "Remove checkout" : "Remove project";
  const previews = props.previews ?? new Map<string, ProjectCheckoutDeletionPreview>();
  const canConfirm = canConfirmProjectGroupRemoval({
    deleteFiles,
    targets: props.targets,
    previews,
    confirmation,
  });
  const actionLabel = projectGroupRemovalActionLabel({
    mode: props.mode,
    deleteFiles,
    previews,
  });
  const completed =
    props.results.length > 0 && props.results.every((result) => result.metadata === "removed");

  return (
    <AlertDialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open && props.isExecuting) return;
        if (!open) {
          setDeleteFiles(false);
          setConfirmation("");
        }
        props.onOpenChange(open);
      }}
    >
      <AlertDialogPopup className="max-w-lg">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle>{action}?</AlertDialogTitle>
          <AlertDialogDescription>
            {props.mode === "checkout"
              ? "Removes this checkout and its threads from Forge. CMMD stays intact."
              : "Removes this project and its threads from Forge. CMMD stays intact."}
          </AlertDialogDescription>
          <ProjectGroupRemovalTargetList
            targets={props.targets}
            previews={previews}
            expanded={deleteFiles}
          />
          <ProjectCheckoutDeletionControl
            checked={deleteFiles}
            confirmation={confirmation}
            disabled={completed || props.isExecuting || props.isPreviewing === true}
            previews={previews}
            onCheckedChange={(checked) => {
              setDeleteFiles(checked);
              if (!checked) setConfirmation("");
              props.onDeleteFilesChange?.(checked);
            }}
            onRetry={() => props.onDeleteFilesChange?.(true)}
            onConfirmationChange={setConfirmation}
          />
          {props.results.length > 0 ? (
            <ul className="grid gap-1 text-xs text-muted-foreground">
              {props.results.map((result) => (
                <li key={`${result.target.environmentId}:${result.target.projectId}`}>
                  {result.target.label}: {projectGroupRemovalResultLabel(result)}
                </li>
              ))}
            </ul>
          ) : null}
          {props.isPreviewing ? (
            <p className="text-xs text-muted-foreground">Checking checkout files...</p>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {completed ? (
            <Button className="min-h-touch md:min-h-8" onClick={() => props.onOpenChange(false)}>
              Done
            </Button>
          ) : (
            <>
              <AlertDialogClose
                render={
                  <Button
                    className="min-h-touch md:min-h-8"
                    variant="outline"
                    disabled={props.isExecuting}
                  />
                }
              >
                Cancel
              </AlertDialogClose>
              <Button
                className="min-h-touch md:min-h-8"
                variant="destructive"
                disabled={props.isExecuting || props.isPreviewing === true || !canConfirm}
                onClick={() => props.onConfirm({ deleteFiles, confirmation })}
              >
                {props.isExecuting ? "Removing..." : actionLabel}
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
